import { useCallback, useEffect, useReducer, useRef } from 'react'
import apiClient from './api/client'
import { errorMessage, isApiError } from './api/ApiError'
import {
  HILO_POLL_MS, aplicar, debeSondear, esDefinitivo, hiloInicial, vista,
  type EstadoHilo, type EventoHilo,
} from './hilo'
import type { ConversationDetail } from './types/portal'

/**
 * La conversación abierta, al día y sin trabarse con la red lenta.
 *
 * Las decisiones —qué se muestra, qué respuesta vale, cuándo sale el sondeo,
 * qué fallo suelta la conversación— viven en `hilo.ts`, puras y probadas en
 * Node; acá solo se cablean. Lo usan la bandeja y En vivo: dos copias del
 * mismo cableado se desalinean en cuanto una cambie, que es lo que ya pasó con
 * el Copiloto (DEF-029).
 *
 * Tres caminos piden el detalle, y solo uno espera su turno:
 * - `abrir`: al cambiar de conversación y con «Reintentar»;
 * - `accion`: después de algo que hizo el asesor (enviar, subir, devolver), que
 *   tiene que verse ya y no al próximo sondeo;
 * - `sondeo`: cada `HILO_POLL_MS`, solo sin otro pedido en camino.
 */

export interface Hilo {
  detail: ConversationDetail | null
  /** No hay nada a la vista y viene en camino. */
  cargando: boolean
  /** No hay nada a la vista y el último pedido falló. */
  error: string | null
  /** La conversación se ve, pero dejó de actualizarse. */
  sinConexion: boolean
  /** Volver a pedirlo ya, después de una acción del asesor. */
  refrescar: () => void
  /** «Reintentar» del panel de error. */
  reintentar: () => void
  /** Un cambio local, como la burbuja optimista. Lo que ya estaba en camino no lo pisa. */
  editar: (actualizar: (detail: ConversationDetail | null) => ConversationDetail | null) => void
}

export interface OpcionesHilo {
  /** Mientras es true el sondeo no sale (el asesor está enviando). */
  pausado?: boolean
  /** El texto del panel de error; por defecto, el del propio error. */
  mensajeDeError?: (err: unknown) => string
  /** Se llama con cada fallo, después de aplicarlo. */
  alFallar?: (err: unknown) => void
}

type Motivo = 'abrir' | 'accion' | 'sondeo'

function reducir(estado: EstadoHilo<ConversationDetail>,
                 evento: EventoHilo<ConversationDetail>): EstadoHilo<ConversationDetail> {
  return aplicar(estado, evento)
}

export function useHilo(conversationId: string | null, opciones: OpcionesHilo = {}): Hilo {
  const [estado, despachar] = useReducer(reducir, undefined, () => hiloInicial<ConversationDetail>())
  const abierta = useRef(conversationId)
  // El número del último pedido emitido, y cuántos hay en camino.
  const pedidos = useRef(0)
  const enVuelo = useRef(0)
  // Las opciones llegan nuevas en cada render; el sondeo las lee de acá para
  // no rearmar el intervalo cada vez que el asesor empieza o termina de enviar.
  const opcionesRef = useRef(opciones)
  useEffect(() => { opcionesRef.current = opciones })

  const pedir = useCallback(async (motivo: Motivo) => {
    const id = abierta.current
    if (!id) return
    if (motivo === 'sondeo'
        && !debeSondear(enVuelo.current, Boolean(opcionesRef.current.pausado))) return
    const numero = ++pedidos.current
    enVuelo.current += 1
    try {
      // Solo la sesión en curso: este pedido se repite cada 2,5 s y la
      // conversación entera llegó a pesar 233 KB (TEST, 05-oct-2026). El
      // historial lo pide el hilo cuando el asesor lo abre.
      const res = await apiClient.get<ConversationDetail>(`/conversations/${id}?vista=sesion`)
      despachar({ tipo: 'respuesta', id, pedido: numero, detalle: res.data })
    } catch (err) {
      const status = isApiError(err) ? err.status : 0
      const codigo = isApiError(err) ? err.code : ''
      const mensaje = opcionesRef.current.mensajeDeError?.(err)
        ?? errorMessage(err, 'No se pudo cargar la conversación.')
      despachar({ tipo: 'fallo', id, pedido: numero, mensaje,
                  definitivo: esDefinitivo(status, codigo) })
      opcionesRef.current.alFallar?.(err)
    } finally {
      enVuelo.current -= 1
    }
  }, [])

  // Otra conversación: lo anterior se va y se pide de entrada.
  useEffect(() => {
    abierta.current = conversationId
    despachar({ tipo: 'abrir', id: conversationId })
    if (conversationId) void pedir('abrir')
  }, [conversationId, pedir])

  useEffect(() => {
    if (!conversationId) return
    const t = setInterval(() => { void pedir('sondeo') }, HILO_POLL_MS)
    return () => clearInterval(t)
  }, [conversationId, pedir])

  const refrescar = useCallback(() => { void pedir('accion') }, [pedir])
  const reintentar = useCallback(() => {
    despachar({ tipo: 'abrir', id: abierta.current })
    void pedir('abrir')
  }, [pedir])
  const editar = useCallback((actualizar: Parameters<Hilo['editar']>[0]) => {
    despachar({ tipo: 'editar', pedido: pedidos.current, actualizar })
  }, [])

  const v = vista(estado)
  return {
    detail: v.detalle,
    cargando: v.cargando,
    error: v.error,
    sinConexion: v.sinConexion,
    refrescar,
    reintentar,
    editar,
  }
}
